import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import CargoReceiptSender from "./CargoReceiptSender";
import SelfPickup from "./SelfPickup";
import AgentNotification from "./AgentNotification";
import { useTheme } from "@emotion/react";
import { isMobile } from "react-device-detect";

import { GetStationDetailById, GetFinalStationList } from "api/stations";
import { enqueuePickupNotification } from "api/whatsapp";
import { PatchPackageSomeInfo, GetSumPackagesForWhatsApp } from "api/packages";
import Swal from "sweetalert2";
import { DataGridCustomToolbarClientMode } from "components/DataGridCustomToolbar";
import {
  copyTextToClipboard,
  playAlertSound,
  playCorrectSound,
  playIncorrectSound,
} from "utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import SendIcon from "@mui/icons-material/Send";
import useStationRefresh from "hooks/useStationRefresh";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import BentoTabs from "components/bento/BentoTabs";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { PURPLE_DARK } from "components/bento/bentoTokens";
import { getVisibleTabs } from "utils/pagePermissions";

const ALL_WHATSAPP_TABS = [
  { key: "selfPickup", slug: "whatsapp/self-pickup", i18nKey: "whatsappPage.tabs.selfPickup" },
  { key: "pickupNotification", slug: "whatsapp/pickup-notification", i18nKey: "whatsappPage.tabs.pickupNotification" },
  { key: "cargoReceipt", slug: "whatsapp/cargo-receipt", i18nKey: "whatsappPage.tabs.cargoReceipt" },
  { key: "agentNotification", slug: "whatsapp/agent-notification", i18nKey: "whatsappPage.tabs.agentNotification" },
];

const WhatsApp = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [stationList, setStationList] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [rows, setRows] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [filteredRows, setFilteredRows] = useState(null);

  // values to be sent to the backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");

  const { user: _user, defaultStation: _defaultStation } = useStationRefresh();
  const isAgentUser = _user?.role === "agent";

  const visibleSlugs = getVisibleTabs(_user, "whatsapp").map((tab) => tab.slug);
  const tabs = ALL_WHATSAPP_TABS.filter((tab) => visibleSlugs.includes(tab.slug));
  // BentoTabs' value/onChange are index-based (not key-based), so activeTab stays
  // an index into the filtered `tabs` array; activeKey is derived for rendering.
  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => {
    if (activeTab >= tabs.length) setActiveTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length]);
  const activeKey = tabs[activeTab]?.key;

  function RefreshPackagesList(station) {
    setIsLoading(true);
    GetSumPackagesForWhatsApp(station).then((res) => {
      if (res.status === 200) {
        setRows(res.data);
      } else {
        setRows([]);
      }
      setIsLoading(false);
    });
  }

  function filterPackage(dataArr, searchValue) {
    if (dataArr) {
      let newArr = dataArr.slice();
      if (searchValue) {
        newArr = newArr.filter((element) => {
          return (
            (element._id.customer.name &&
              element._id.customer.name
                .toLowerCase()
                .includes(searchValue.toLowerCase())) ||
            (element._id.customer.whatsapp &&
              element._id.customer.whatsapp
                .toLowerCase()
                .includes(searchValue.toLowerCase()))
          );
        });
      }
      setFilteredRows(newArr);
    }
  }

  function deleteRow(dataArr, rowId) {
    if (dataArr) {
      let newArr = dataArr.slice();
      if (rowId) {
        newArr = newArr.filter((element) => {
          return element._id.customer._id !== rowId;
        });
      }
      setFilteredRows(newArr);
    }
  }

  useEffect(() => {
    filterPackage(rows, search);
  }, [rows, search]);

  useEffect(() => {
    if (selectedStation) {
      RefreshPackagesList(selectedStation.id);
    }
  }, [selectedStation]);

  useEffect(() => {
    GetFinalStationList().then((res) => {
      setStationList(res.data);
      if (res && res.status === 200) {
        setStationList(res.data);
        // let arr = res.data;
        // let validStation = [];
        // if (Array.isArray(arr)) {
        //   arr.forEach((finalStation) => {
        //     // console.log("finalStation", finalStation);
        //     _user.stations.forEach((userStation) => {
        //       // console.log("userStation", userStation);
        //       if (finalStation.id === userStation.id)
        //         validStation.push(finalStation);
        //     });
        //   });
        // }
        // if (validStation) setStationList(validStation);
      }
      setSelectedStation(_defaultStation);
    });
    // Station list comes from the server, so it's already fresh — no need to
    // re-run on profile refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendWhatsappMessage = async (message, dataObj) => {
    setIsSending(true);
    const customerId = dataObj._id.customer._id;
    const trackingNumbers = (dataObj.packageDetail || []).map((item) => item.tracking);

    try {
      const queueRes = await enqueuePickupNotification(
        customerId,
        message,
        trackingNumbers,
        selectedStation?.id
      );

      if (queueRes && queueRes.success && queueRes.data?.status === "queued") {
        playCorrectSound();
        deleteRow(filteredRows, queueRes.data?.removeKeys?.customerId || dataObj._id.customer._id);
        Swal.fire({
          text: "Queued for sending",
          icon: "success",
          position: "top-right",
          timer: 1200,
          toast: true,
          showConfirmButton: false,
        });
      } else {
        throw new Error(queueRes?.message || "Failed to queue WhatsApp message");
      }
    } catch (error) {
      console.error("❌ WhatsApp group instance sending failed:", error);
      playIncorrectSound();
      Swal.fire({
        title: "WhatsApp Queue Failed",
        icon: "error",
        text: `Failed to queue message: ${error.message}`,
        timer: 5000,
        showConfirmButton: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const updatePackage = (rows, dataObj) => {
    playAlertSound();
    Swal.fire({
      title: `ສົ່ງຂໍ້ຄວາມແຈ້ງ ${dataObj._id.customer.name} ແລ້ວບໍ?`,
      text: `Have you notified ${dataObj._id.customer.name} yet?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: " Yes ",
      cancelButtonText: " Not yet ",
    }).then((result) => {
      if (result.isConfirmed) {
        const packageDetail = dataObj.packageDetail;
        for (let index = 0; index < packageDetail.length; index++) {
          PatchPackageSomeInfo(
            packageDetail[index]._id,
            packageDetail[index].tracking,
            selectedStation.id,
            selectedStation.parcelStatus,
            packageDetail[index].remark,
            false,
            true
          ).then((res) => {
            if (
              res && res.success &&
              index === dataObj.packageDetail.length - 1
            ) {
              playCorrectSound();
              deleteRow(rows, dataObj._id.customer._id);
              Swal.fire({
                text: "Packages updated successfully",
                icon: "success",
                position: "top-right",
                timer: 1200,
                toast: true,
                showConfirmButton: false,
              });
            }
            if (res && !res.success) {
              playIncorrectSound();
              Swal.fire({
                title: "Package Update Failed",
                icon: "error",
                text: res.message,
                timer: 5000,
                showConfirmButton: true,
              });
              console.log(res);
            }
          });
        }
      }
    });
  };

  const columns = [
    {
      field: "_id",
      headerName: "ID",
      flex: 1,
      valueGetter: (params) => params.row._id.customer._id,
      hide: true,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 0.7,
      valueGetter: (params) => params.row._id.customer.name,
    },
    {
      field: "whatsapp",
      headerName: "Whatsapp",
      flex: 1,
      valueGetter: (params) => params.row._id.customer.whatsapp,
      renderCell: (cellValues) => {
        const whatsappNumber = cellValues.value;
        const smsMessage = cellValues.row.smsMessage;

        return (
          <a
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => copyTextToClipboard(smsMessage)}
            style={{ textDecoration: "none", color: PURPLE_DARK }}
          >
            {whatsappNumber}
          </a>
        );
      },
    },

    {
      field: "smsMessage",
      headerName: "Message",
      flex: 3,
      hide: isMobile,
    },
    {
      field: "totalPackages",
      headerName: "item(s).",
      flex: 0.5,
    },
    {
      field: "totalShippingFee",
      headerName: "Fee",
      flex: 0.5,
      hide: isMobile,
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      flex: 0.5,
      hide: isMobile,
    },
    {
      field: "copy",
      headerName: "Copy",
      flex: 0.5,
      hide: isMobile && !isAgentUser,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outlined"
            onClick={() => {
              copyTextToClipboard(cellValues.row.smsMessage);
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <ContentCopyIcon />
          </IconButton>
        );
      },
    },
    {
      field: "send",
      headerName: "Send",
      flex: 0.5,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outlined"
            disabled={isSending}
            onClick={() => {
              sendWhatsappMessage(cellValues.row.smsMessage, cellValues.row);
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <SendIcon />
          </IconButton>
        );
      },
    },
    {
      field: "success",
      headerName: "Success",
      flex: 0.5,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outlined"
            onClick={() => {
              updatePackage(filteredRows, cellValues.row);
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <CheckIcon />
          </IconButton>
        );
      },
    },
  ];

  // Conditionally filter columns based on isMobile state
  const filteredColumns = isAgentUser
    ? columns.filter((column) => column.field !== "send")
    : columns;

  return (
    <PageShell title={t("whatsappPage.title")} subtitle={t("whatsappPage.subtitle")}>
      <BentoTabs
        tabs={tabs.map((tab) => ({ key: tab.key, label: t(tab.i18nKey) }))}
        value={activeTab}
        onChange={setActiveTab}
        sx={{ display: "inline-flex", mb: "22px", maxWidth: "100%", overflowX: "auto" }}
      />

      {activeKey === "selfPickup" && <SelfPickup />}
      {activeKey === "cargoReceipt" && <CargoReceiptSender />}
      {activeKey === "agentNotification" && <AgentNotification />}
      {activeKey === "pickupNotification" && (
        <>
          <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
            <FormControl sx={{ minWidth: 260 }}>
              <InputLabel>Select station</InputLabel>
              <Select
                label="Select station"
                value={selectedStation ? selectedStation.id : ""}
                onChange={(e) => {
                  GetStationDetailById(e.target.value).then((res) => {
                    if (res && res.success) {
                      setSelectedStation(res.data);
                    }
                  });
                }}
                required
              >
                {stationList &&
                  stationList.map(({ id, name }) => {
                    return (
                      <MenuItem key={id} value={id}>
                        {id} - {name}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </BentoCard>

          {/*  Data Grid  */}
          <Box height="74vh">
            <StyledDataGrid
              loading={isLoading}
              getRowId={(row) => row._id.customer._id}
              rows={filteredRows || []}
              columns={filteredColumns}
              rowCount={filteredRows ? filteredRows.length : 0}
              rowsPerPageOptions={[20, 50, 100]}
              pagination
              page={page}
              pageSize={pageSize}
              paginationMode="client"
              sortingMode="client"
              filterMode="client"
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              // onSortModelChange={(newSortModel) => setSort(...newSortModel)}
              components={{ Toolbar: DataGridCustomToolbarClientMode }}
              componentsProps={{
                toolbar: { search, setSearch },
              }}
              containerSx={{ height: "100%" }}
              autoHeight={false}
            />
          </Box>
        </>
      )}
    </PageShell>
  );
};

export default WhatsApp;
